import { forwardRef, TextareaHTMLAttributes, useRef } from 'react';

import { textAreaOverridesClass } from './TextArea.css';
import { useInputIds } from '../../hooks/useInputIds';
import { useValidationErrorEvent } from '../../hooks/useValidationErrorEvent';
import { mergeRefs } from '../../util';
import { getClassNames } from '../../util/css';
import { InputBaseProps, inputRecipe } from '../Input/Input';
import { InputError } from '../Input/InputError';
import { Hint } from '../Input/InputHint';
import { Label } from '../Input/InputLabel';
import { Stack } from '../Stack/Stack';

type TextAreaProps = InputBaseProps & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'name'>;

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ error, hint, isDisabled, isOptional, label, name, ...rest }, ref) => {
    const localRef = useRef<HTMLTextAreaElement>(null);
    const mergedRef = mergeRefs([ref, localRef]);

    useValidationErrorEvent({ error, name }, localRef);

    const { inputId, describedBy, errorId, hintId } = useInputIds({ error, hint });
    return (
      <Stack gap="2">
        <Label htmlFor={inputId} isOptional={isOptional}>
          {label}
        </Label>
        <Stack.Item grow>
          <textarea
            aria-describedby={describedBy}
            aria-invalid={error ? true : undefined}
            aria-required={!isOptional}
            className={getClassNames(inputRecipe({ isInvalid: !!error }), textAreaOverridesClass)}
            disabled={isDisabled}
            id={inputId}
            name={name}
            ref={mergedRef}
            {...rest}
          />
          {error ? <InputError id={errorId}>{error}</InputError> : null}
        </Stack.Item>
        {hint ? <Hint id={hintId}>{hint}</Hint> : null}
      </Stack>
    );
  },
);

TextArea.displayName = 'TextArea';
